import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  Mail,
  LockKeyhole,
  Eye,
  EyeOff,
  ClipboardCheck,
  ShieldCheck,
  Building2,
  HardHat,
  Leaf,
  Ship,
} from "lucide-react";

import gsap from "gsap";

const LOGO_TWO = "/assets/images/logo2.png";

const services = {
  registry: {
    label: "Registry Portal",
    title: "Registry",
    description: "Enterprise registration, licence records and corporate verification.",
    dashboard: "/portal/registry-portal",
    icon: Building2,
  },
  engineering: {
    label: "Engineering Portal",
    title: "Engineering",
    description: "Development applications, inspections and engineering service tracking.",
    dashboard: "/portal/engineering-portal",
    icon: HardHat,
  },
  hse: {
    label: "HSE Portal",
    title: "HSE",
    description: "HSE submissions, inspection requests and incident reporting.",
    dashboard: "/portal/hse-portal",
    icon: Leaf,
  },
  operations: {
    label: "Operations Portal",
    title: "Operations",
    description: "Cargo movement, goods transfer and operational approvals.",
    dashboard: "/portal/operations-portal",
    icon: Ship,
  },
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginRef = useRef(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedService, setSelectedService] = useState("registry");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const serviceFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const service = params.get("service");
    return services[service] ? service : "registry";
  }, [location.search]);

  const activeService = services[selectedService];
  const ActiveIcon = activeService.icon;

  useEffect(() => {
    setSelectedService(serviceFromUrl);
  }, [serviceFromUrl]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("ogfzaPortalLoggedIn") === "true";
    const savedService = localStorage.getItem("ogfzaPortalService");

    if (isLoggedIn && services[savedService]) {
      navigate(services[savedService].dashboard);
    }
  }, [navigate]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".login-fade", {
        opacity: 0,
        y: 18,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.06,
      });
    }, loginRef);

    return () => ctx.revert();
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    localStorage.setItem("ogfzaPortalLoggedIn", "true");
    localStorage.setItem("ogfzaPortalUser", email);
    localStorage.setItem("ogfzaPortalService", selectedService);

    navigate(activeService.dashboard);
  };

  return (
    <main
      ref={loginRef}
      className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4 py-6 font-sans"
    >
      <section className="login-fade w-full max-w-[860px] bg-white rounded-[20px] shadow-[0_18px_55px_rgba(2,15,46,0.11)] overflow-hidden grid lg:grid-cols-2">
        <div className="px-5 md:px-8 py-7 md:py-8 flex flex-col justify-center">
          <div className="flex justify-center mb-4">
            <Link to="/" aria-label="Go to OGFZA homepage">
              <img
                src={LOGO_TWO}
                alt="OGFZA Logo"
                className="w-20 md:w-24 h-auto object-contain hover:scale-105 transition-all duration-300"
              />
            </Link>
          </div>

          <h1 className="text-center text-xl md:text-2xl font-black text-[#026b07] tracking-tight mb-1">
            WELCOME
          </h1>

          <p className="text-center text-xs text-slate-500 mb-5">
            Sign in to continue to your selected OGFZA service portal.
          </p>

          <form
            onSubmit={handleLogin}
            className="max-w-[350px] mx-auto w-full space-y-3"
          >
            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 mb-1.5">
                Select Portal
              </span>

              <select
                value={selectedService}
                onChange={(e) => {
                  setSelectedService(e.target.value);
                  setError("");
                }}
                className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10"
              >
                <option value="registry">Registry Portal</option>
                <option value="engineering">Engineering Portal</option>
                <option value="hse">HSE Portal</option>
                <option value="operations">Operations Portal</option>
              </select>
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 mb-1.5">
                Email Address
              </span>

              <div className="relative">
                <Mail
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#026b07]"
                />

                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your email address"
                  className="w-full rounded-lg border border-slate-300 bg-slate-50 pl-9 pr-3 py-3 text-xs text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-slate-700 mb-1.5">
                Password
              </span>

              <div className="relative">
                <LockKeyhole
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#026b07]"
                />

                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your password"
                  className="w-full rounded-lg border border-slate-300 bg-slate-50 pl-9 pr-10 py-3 text-xs text-slate-800 outline-none transition-all duration-200 focus:border-[#201182] focus:ring-2 focus:ring-[#201182]/10"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#201182] transition-colors"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </label>

            <div className="flex items-center justify-end">
              <button
                type="button"
                className="text-xs font-medium text-[#026b07] hover:text-[#201182] transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 font-medium">
                {error}
              </div>
            )}

            <div className="space-y-3 pt-1">
              <button
                type="submit"
                className="w-full rounded-lg bg-[#026b07] py-3 text-white text-xs font-black tracking-wide hover:bg-[#201182] transition-all duration-300 shadow-md"
              >
                LOGIN
              </button>

              <div className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#026b07] px-4 py-3 text-[11px] font-bold text-[#026b07]">
                <ClipboardCheck size={14} />
                {activeService.label}
              </div>
            </div>
          </form>
        </div>

        <div className="hidden lg:flex relative bg-[#026b07] text-white min-h-[500px] rounded-tl-[60px] rounded-bl-[60px] overflow-hidden items-center justify-center px-7">
          <div className="absolute inset-0 bg-gradient-to-br from-[#026b07] via-[#074f22] to-[#201182]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#f2af11]/20 rounded-full blur-[90px]" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-[90px]" />

          <div className="relative text-center flex flex-col items-center justify-center">
            <div className="mx-auto mb-5 w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
              <ActiveIcon size={25} className="text-[#f2af11]" />
            </div>

            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-wide leading-tight">
              OGFZA
              <br />
              {activeService.title}
              <br />
              Portal
            </h2>

            <div className="w-20 h-[3px] bg-[#f2af11] rounded-full mx-auto my-5" />

            <p className="text-base font-serif">{activeService.description}</p>

            <p className="text-white/80 italic mt-2 text-xs">
              Oil & Gas Free Zones Authority
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Login;